import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Typography,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

// eslint-disable-next-line max-lines-per-function
const PromptDisplay = ({ fullPrompt }) => {
  const [expanded, setExpanded] = useState(false);

  if (!fullPrompt) return null;

  const { systemPrompt, userMessage, promptTemplate, toolInteractions, toolsUsed } = fullPrompt;

  // Debug logging
  console.log('PromptDisplay received fullPrompt:', {
    hasSystemPrompt: !!systemPrompt,
    hasUserMessage: !!userMessage,
    hasPromptTemplate: !!promptTemplate,
    hasToolInteractions: !!toolInteractions,
    toolInteractionsLength: toolInteractions?.length,
    hasToolsUsed: !!toolsUsed,
    toolsUsedLength: toolsUsed?.length,
    fullPromptKeys: Object.keys(fullPrompt)
  });

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: 1,
        borderColor: 'grey.300',
      }}
    >
      <Button
        size="small"
        variant="text"
        onClick={() => setExpanded(!expanded)}
        startIcon={<CodeIcon sx={{ fontSize: 16 }} />}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          fontSize: '0.8rem',
          p: 0.5,
          minWidth: 'auto',
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        View prompt sent to AI
      </Button>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          {/* Prompt Template Info */}
          {promptTemplate && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                  AI Prompt Template:
                </Typography>
                <Chip
                  label={promptTemplate.name}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20 }}
                />
                {promptTemplate.version && (
                  <Chip
                    label={`v${promptTemplate.version}`}
                    size="small"
                    sx={{ height: 20 }}
                  />
                )}
              </Box>
              {promptTemplate.description && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ml: 1 }}>
                  {promptTemplate.description}
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* System Prompt */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              System Instructions:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                mt: 0.5,
                p: 1.5,
                bgcolor: 'grey.100',
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  display: 'block',
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                }}
              >
                {systemPrompt}
              </Typography>
            </Paper>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* User Message */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              User Message:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                mt: 0.5,
                p: 1.5,
                bgcolor: 'primary.50',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  display: 'block',
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                }}
              >
                {userMessage}
              </Typography>
            </Paper>
          </Box>

          {/* MCP Tool Interactions */}
          {toolInteractions && toolInteractions.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1 }}>
                  MCP Tool Interactions:
                </Typography>

                {toolInteractions.map((step, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: 'grey.50',
                    }}
                  >
                    {/* Tool Call */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        Tool Call #{index + 1}: {step.action?.tool || 'Unknown Tool'}
                      </Typography>
                      {step.action?.toolInput && (
                        <Paper
                          sx={{
                            mt: 0.5,
                            p: 1,
                            bgcolor: 'info.50',
                            border: 1,
                            borderColor: 'info.200',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              display: 'block',
                              fontSize: '0.7rem',
                            }}
                          >
                            Input: {JSON.stringify(step.action.toolInput, null, 2)}
                          </Typography>
                        </Paper>
                      )}
                    </Box>

                    {/* Tool Response */}
                    {step.observation && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          Tool Response:
                        </Typography>
                        <Paper
                          sx={{
                            mt: 0.5,
                            p: 1,
                            bgcolor: 'success.50',
                            border: 1,
                            borderColor: 'success.200',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              display: 'block',
                              fontSize: '0.7rem',
                            }}
                          >
                            {typeof step.observation === 'string'
                              ? step.observation
                              : JSON.stringify(step.observation, null, 2)}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {/* Tools Summary */}
          {toolsUsed && toolsUsed.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                  Tools Summary:
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {toolsUsed.map((tool, index) => (
                    <Chip
                      key={index}
                      label={`${tool.tool}${tool.resourcesAccessed?.length ? ` (${tool.resourcesAccessed.length} resources)` : ''}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PromptDisplay;